import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepButton,
  Stepper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ButtonWithConfirmation from "../../common/ButtonWithConfirmation";
import ProductFieldOption from "./ProductFieldOption";
import VariantOptionSizeOverrideGrid from "./VariantOptionSizeOverrideGrid";
import VariantOptionSizeOverrideCellDialog from "./VariantOptionSizeOverrideCellDialog";
import { capitalize, getVariantOptionSurchargeForDisplay, isNumeric } from "../../common/util";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import { FIELDS } from "../../common/constants";
import {
  clearVariantOptionSizeOverrides,
  getVariantOptionSizeOverrideCount,
  isPercentageSizeCell,
  isSameVariantOptionPricing,
} from "../../common/variantSizeOverride";

export const CUSTOM_FIELD_VALUE = "__custom__";

/**
 * Build rank position options for the Select.
 * orderableEntries: array of [key, value] sorted by rank desc (first = after Size).
 * Returns [{ value: index, label: string }].
 */
function getRankPositionOptions(orderableEntries) {
  if (!orderableEntries?.length) return [];
  const options = [{ value: 0, label: `After "Size"` }];
  orderableEntries.forEach(([k, v], i) => {
    options.push({ value: i + 1, label: `After "${v?.name ?? capitalize(k)}"` });
  });
  return options;
}

/**
 * Compute new orderable keys after moving variantKey to targetPositionIndex.
 */
function reorderOrderableKeys(orderableEntries, variantKey, targetPositionIndex) {
  const keys = orderableEntries.map(([k]) => k);
  const withoutCurrent = keys.filter((k) => k !== variantKey);
  withoutCurrent.splice(targetPositionIndex, 0, variantKey);
  return withoutCurrent;
}

export default function VariantFieldEditorDialog(props) {
  const {
    open,
    onClose,
    variantKey,
    variants,
    orderableEntries = [],
    fieldsNoOrderArrows = ["photo", "inscription", "notes"],
    onSave,
    onDelete,
    disabled = [],
    showSizeOverrideGrid: sizeOverrideGridEnabled = false,
  } = props;

  const isAddMode = open && (variantKey == null || variantKey === "");
  const [pendingVariantKey, setPendingVariantKey] = useState(null);
  const [pendingVariant, setPendingVariant] = useState(null);

  const variant = variants?.[variantKey];
  const effectiveVariant = variantKey && variant ? variant : pendingVariant;
  const effectiveVariantKey =
    variantKey != null && variantKey !== "" ? variantKey : pendingVariantKey;

  const isOrderable =
    (effectiveVariantKey &&
      effectiveVariantKey !== "size" &&
      !fieldsNoOrderArrows.includes(effectiveVariantKey)) ||
    (isAddMode && effectiveVariant?.field?.type === "select");
  const isSelectField = effectiveVariant?.field?.type === "select";
  const isTextField = effectiveVariant?.field?.type === "text";
  const isPhotoField = effectiveVariant?.field?.type === "photo";
  const showFieldOptionsTab =
    isSelectField &&
    (effectiveVariantKey == null || !fieldsNoOrderArrows.includes(effectiveVariantKey));

  const TEXT_MAX_LENGTH_MIN = 0;
  const TEXT_MAX_LENGTH_MAX = 500;
  const PHOTO_MAX_MIN = 1;
  const PHOTO_MAX_MAX = 10;
  const SELECT_MIN_MIN = 1;
  const SELECT_MIN_MAX = 10;
  const SELECT_MAX_MIN = 1;
  const SELECT_MAX_MAX = 10;

  const [tab, setTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectionType, setSelectionType] = useState("single"); // "single" | "multi" for select fields
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(1);
  const [required, setRequired] = useState(true);
  const [rankPositionIndex, setRankPositionIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [photoSurchargeEnabled, setPhotoSurchargeEnabled] = useState(false);
  const [photoSurchargeAmount, setPhotoSurchargeAmount] = useState("");
  const [photoSurchargeError, setPhotoSurchargeError] = useState("");
  const [selectedOverrideCell, setSelectedOverrideCell] = useState(null);
  const [optionEditIndex, setOptionEditIndex] = useState(null);
  const [optionPricingIndex, setOptionPricingIndex] = useState(null);
  const [optionDeleteIndex, setOptionDeleteIndex] = useState(null);
  const [percentOverrideBlocked, setPercentOverrideBlocked] = useState(false);

  const orderableKeys = orderableEntries.map(([k]) => k);
  const currentRankIndex = orderableKeys.indexOf(effectiveVariantKey);
  const rankPositionOptions = getRankPositionOptions(orderableEntries);

  const sizeChart = Array.isArray(variants?.size?.field?.options)
    ? variants.size.field.options
    : [];
  const showSizeOverrideGrid =
    sizeOverrideGridEnabled && isSelectField && effectiveVariantKey !== "size";

  // When opening in add mode, start with no selection (user picks preset or custom in step 1)
  useEffect(() => {
    if (!open || variantKey != null) return;
    setPendingVariantKey(null);
    setPendingVariant(null);
    setActiveStep(0);
  }, [open, variantKey]);

  useEffect(() => {
    if (open) return;
    setSelectedOverrideCell(null);
    setOptionEditIndex(null);
    setOptionPricingIndex(null);
    setOptionDeleteIndex(null);
    setPercentOverrideBlocked(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const source = effectiveVariant;
    if (variantKey && variant) {
      setTab(0);
      setName(variant.name ?? "");
      setDescription(variant.description ?? "");
      const rawMax = variant.field?.max;
      const rawMin = variant.field?.min;
      if (variant.field?.type === "select") {
        const isMulti = typeof rawMax === "number" && rawMax > 1;
        setSelectionType(isMulti ? "multi" : "single");
        if (isMulti) {
          setMin(
            typeof rawMin === "number"
              ? Math.min(SELECT_MIN_MAX, Math.max(SELECT_MIN_MIN, rawMin))
              : 1
          );
          setMax(Math.min(SELECT_MAX_MAX, Math.max(SELECT_MAX_MIN, rawMax)));
        } else {
          setMin(1);
          setMax(1);
        }
      } else if (typeof rawMax === "number") {
        if (variant.field?.type === "text") {
          setMax(Math.min(TEXT_MAX_LENGTH_MAX, Math.max(TEXT_MAX_LENGTH_MIN, rawMax)));
        } else if (variant.field?.type === "photo") {
          setMax(Math.min(PHOTO_MAX_MAX, Math.max(PHOTO_MAX_MIN, rawMax)));
        }
      } else {
        setMax(variant.field?.type === "text" ? 150 : variant.field?.type === "photo" ? 1 : 1);
      }
      setRequired(Boolean(variant.required));
      setRankPositionIndex(currentRankIndex >= 0 ? currentRankIndex : orderableKeys.length);
      setOptions(Array.isArray(variant.field?.options) ? [...variant.field.options] : []);
      if (variant.field?.type === "photo" && variant.pricing) {
        setPhotoSurchargeEnabled(true);
        setPhotoSurchargeAmount(variant.pricing.amount ?? "");
      } else if (variant.field?.type === "photo") {
        setPhotoSurchargeEnabled(false);
        setPhotoSurchargeAmount("");
      }
      setPhotoSurchargeError("");
    } else if (isAddMode && source) {
      setTab(0);
      setName(source.name ?? "");
      setDescription(source.description ?? "");
      const rawMax = source.field?.max;
      const rawMin = source.field?.min;
      if (source.field?.type === "select") {
        const isMulti = typeof rawMax === "number" && rawMax > 1;
        setSelectionType(isMulti ? "multi" : "single");
        if (isMulti) {
          setMin(
            typeof rawMin === "number"
              ? Math.min(SELECT_MIN_MAX, Math.max(SELECT_MIN_MIN, rawMin))
              : 1
          );
          setMax(Math.min(SELECT_MAX_MAX, Math.max(SELECT_MAX_MIN, rawMax)));
        } else {
          setMin(1);
          setMax(1);
        }
      } else if (typeof rawMax === "number") {
        if (source.field?.type === "text") {
          setMax(Math.min(TEXT_MAX_LENGTH_MAX, Math.max(TEXT_MAX_LENGTH_MIN, rawMax)));
        } else if (source.field?.type === "photo") {
          setMax(Math.min(PHOTO_MAX_MAX, Math.max(PHOTO_MAX_MIN, rawMax)));
        }
      } else {
        setMax(source.field?.type === "text" ? 150 : source.field?.type === "photo" ? 1 : 1);
      }
      setRequired(Boolean(source.required));
      setRankPositionIndex(currentRankIndex >= 0 ? currentRankIndex : orderableKeys.length);
      setOptions(Array.isArray(source.field?.options) ? [...source.field.options] : []);
      if (source.field?.type === "photo" && source.pricing) {
        setPhotoSurchargeEnabled(true);
        setPhotoSurchargeAmount(source.pricing.amount ?? "");
      } else if (source.field?.type === "photo") {
        setPhotoSurchargeEnabled(false);
        setPhotoSurchargeAmount("");
      }
      setPhotoSurchargeError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from variant/pendingVariant props
  }, [
    open,
    variantKey,
    variant?.name,
    variant?.description,
    variant?.required,
    variant?.field?.min,
    variant?.field?.max,
    variant?.field?.options,
    variant?.pricing,
    isAddMode,
    pendingVariant?.name,
    pendingVariant?.description,
    pendingVariant?.required,
    pendingVariant?.field?.min,
    pendingVariant?.field?.max,
    pendingVariant?.field?.options,
    pendingVariant?.pricing,
    currentRankIndex,
    orderableKeys.length,
  ]);

  const handleSave = (e) => {
    e?.preventDefault();
    // In add mode, only allow save when on the final step (Options); ignore accidental submit on step 0 or 1
    if (isAddMode && activeStep < 2) return;
    const saveKey =
      variantKey != null && variantKey !== ""
        ? variantKey
        : pendingVariantKey != null && pendingVariantKey !== ""
          ? pendingVariantKey
          : name.trim().toLowerCase().replace(/\s+/g, "-");
    const canSaveEdit = variantKey && variant;
    const canSaveAdd = isAddMode && effectiveVariant;
    if (!canSaveEdit && !canSaveAdd) return;
    if (!saveKey && canSaveAdd && !name.trim()) return;
    if (isPhotoField && photoSurchargeEnabled) {
      if (!photoSurchargeAmount || !String(photoSurchargeAmount).trim()) {
        setPhotoSurchargeError("Enter a surcharge amount");
        return;
      }
      if (!isNumeric(+photoSurchargeAmount)) {
        setPhotoSurchargeError("Invalid surcharge amount");
        return;
      }
    }
    const maxNum = typeof max === "number" ? max : parseInt(max, 10);
    const minNum = typeof min === "number" ? min : parseInt(min, 10);
    let clampedMin = 1;
    let clampedMax = 1;
    if (isTextField) {
      clampedMax = Number.isNaN(maxNum)
        ? 150
        : Math.min(TEXT_MAX_LENGTH_MAX, Math.max(TEXT_MAX_LENGTH_MIN, maxNum));
    } else if (isPhotoField) {
      clampedMax = Number.isNaN(maxNum)
        ? 1
        : Math.min(PHOTO_MAX_MAX, Math.max(PHOTO_MAX_MIN, maxNum));
    } else if (isSelectField) {
      if (selectionType === "single") {
        clampedMin = 1;
        clampedMax = 1;
      } else {
        clampedMin = Number.isNaN(minNum)
          ? 1
          : Math.min(SELECT_MIN_MAX, Math.max(SELECT_MIN_MIN, minNum));
        clampedMax = Number.isNaN(maxNum)
          ? 1
          : Math.min(SELECT_MAX_MAX, Math.max(SELECT_MAX_MIN, maxNum));
        if (clampedMin > clampedMax) clampedMin = clampedMax;
      }
    }
    let photoPricing = undefined;
    if (isPhotoField) {
      if (photoSurchargeEnabled && photoSurchargeAmount && String(photoSurchargeAmount).trim()) {
        photoPricing = { type: "surcharge", amount: String(photoSurchargeAmount).trim() };
      } else {
        photoPricing = null;
      }
    }
    let newOrderableKeys = null;
    if (isOrderable) {
      if (variantKey && variant) {
        newOrderableKeys = reorderOrderableKeys(orderableEntries, variantKey, rankPositionIndex);
      } else if (canSaveAdd && saveKey) {
        const keys = orderableEntries.map(([k]) => k);
        keys.splice(rankPositionIndex, 0, saveKey);
        newOrderableKeys = keys;
      }
    }
    onSave?.({
      variantKey: saveKey,
      name: name.trim(),
      description: description.trim() ?? "",
      min: isSelectField ? clampedMin : undefined,
      max: clampedMax,
      required,
      newOrderableKeys,
      options: isSelectField ? options : undefined,
      pricing: photoPricing,
    });
    if (canSaveAdd) {
      setPendingVariantKey(null);
      setPendingVariant(null);
    }
    onClose?.();
  };

  const handleAddModeSelect = (keyOrCustom) => {
    if (keyOrCustom === "custom") {
      setPendingVariantKey(null);
      setPendingVariant({
        name: "",
        description: "",
        rank: 5,
        required: true,
        field: { type: "select", min: 1, max: 1, options: [] },
      });
    } else {
      const preset = FIELDS[keyOrCustom];
      if (preset) {
        setPendingVariantKey(keyOrCustom);
        setPendingVariant({ ...preset });
      }
    }
  };

  const handleClose = () => {
    if (isAddMode) {
      setPendingVariantKey(null);
      setPendingVariant(null);
      setActiveStep(0);
    }
    onClose?.();
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const canProceedFromDetails = Boolean(name?.trim());
  const handleNext = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (activeStep === 1 && !canProceedFromDetails) return;
    setActiveStep((s) => Math.min(2, s + 1));
  };

  const handleDelete = () => {
    if (variantKey) {
      onDelete?.(variantKey);
      onClose?.();
    }
  };

  if (!open) return null;
  if (!isAddMode && (!variantKey || !variant)) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          maxHeight: "100%",
          height: "100%",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6">
            {isAddMode
              ? `Add Field${effectiveVariant?.name?.trim() ? `: ${effectiveVariant.name.trim()}` : ""}`
              : `Edit Field: ${variant.name || capitalize(variantKey)}`}
          </Typography>
          {effectiveVariant && (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {isSelectField
                ? "select field"
                : isTextField
                  ? "text field"
                  : isPhotoField
                    ? "photo field"
                    : "field"}
            </Typography>
          )}
        </Box>
        <IconButton type="button" aria-label="Close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {!isAddMode && showFieldOptionsTab && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="Details" />
          <Tab label={`Options (${options.length})`} />
        </Tabs>
      )}
      <form
        onSubmit={handleSave}
        style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      >
        <DialogContent sx={{ "&&": { pt: 4 }, flex: 1, overflow: "auto", minHeight: 0 }}>
          {isAddMode && (
            <Box sx={{ pb: 4, mb: 2 }}>
              <Stepper activeStep={activeStep} nonLinear sx={{ pt: 0 }}>
                <Step completed={activeStep > 0}>
                  <StepButton onClick={() => setActiveStep(0)}>Field Type</StepButton>
                </Step>
                <Step completed={activeStep > 1}>
                  <StepButton onClick={() => setActiveStep(1)} disabled={activeStep === 0}>
                    Field Settings
                  </StepButton>
                </Step>
                <Step completed={false}>
                  <StepButton
                    onClick={() => setActiveStep(2)}
                    disabled={activeStep === 0 || (activeStep === 1 && !canProceedFromDetails)}
                  >
                    Field Options
                  </StepButton>
                </Step>
              </Stepper>
            </Box>
          )}
          {isAddMode && activeStep === 0 && (
            <Box sx={{ minHeight: 280, px: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{ flex: 1, minWidth: 0, fontWeight: 600, lineHeight: 1.3 }}
                >
                  Choose a field type to add
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    handleAddModeSelect("custom");
                    handleNext();
                  }}
                  color="secondary"
                  startIcon={<AddIcon />}
                  sx={{ flexShrink: 0, alignSelf: "flex-start" }}
                >
                  Custom field
                </Button>
              </Box>
              <RadioGroup
                value={pendingVariantKey ?? (pendingVariant ? CUSTOM_FIELD_VALUE : "")}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === CUSTOM_FIELD_VALUE) handleAddModeSelect("custom");
                  else if (v) handleAddModeSelect(v);
                }}
                sx={{ mt: 3, width: "100%" }}
              >
                <Box mb={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.4,
                      mb: 0.4,
                    }}
                  >
                    <FormatListBulletedOutlinedIcon
                      sx={{ color: "text.secondary", fontSize: 22, mt: 0.35, flexShrink: 0 }}
                    />

                    <Typography variant="h5">Selection Field</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Allow customers to choose from a list of options
                  </Typography>
                </Box>
                {Object.entries(FIELDS)
                  .filter(([_, val]) => val.field?.type === "select")
                  .map(([key, val]) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio />}
                      label={capitalize(val.name)}
                      disabled={Array.isArray(disabled) && disabled.includes(key)}
                    />
                  ))}
                <Box mb={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.4,
                      mt: 2,
                      mb: 0.4,
                    }}
                  >
                    <PhotoCameraOutlinedIcon
                      sx={{ color: "text.secondary", fontSize: 22, mt: 0.35, flexShrink: 0 }}
                    />

                    <Typography variant="h5">Photo Field</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Allow customers to upload photo(s)
                  </Typography>
                </Box>
                {Object.entries(FIELDS)
                  .filter(([_, val]) => val.field?.type === "photo")
                  .map(([key, val]) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio />}
                      label={capitalize(val.name)}
                      disabled={Array.isArray(disabled) && disabled.includes(key)}
                    />
                  ))}
                <Box mb={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.4,
                      mt: 2,
                      mb: 0.4,
                    }}
                  >
                    <TextFieldsOutlinedIcon
                      sx={{ color: "text.secondary", fontSize: 22, mt: 0.35, flexShrink: 0 }}
                    />

                    <Typography variant="h5">Text Field</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Allow customers to type something
                  </Typography>
                </Box>
                {Object.entries(FIELDS)
                  .filter(([_, val]) => val.field?.type === "text")
                  .map(([key, val]) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio />}
                      label={capitalize(val.name)}
                      disabled={Array.isArray(disabled) && disabled.includes(key)}
                    />
                  ))}
              </RadioGroup>
            </Box>
          )}
          {((isAddMode && activeStep === 1) ||
            (!isAddMode && (!showFieldOptionsTab || tab === 0))) && (
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" flexDirection="column" gap={4}>
                  <TextField
                    label="Field Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    variant="outlined"
                    fullWidth
                    disabled={!isAddMode}
                  />
                  <TextField
                    label="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={1}
                    inputProps={{ maxLength: 100 }}
                    helperText={
                      "A short description to help guide customers what this field is about"
                    }
                  />
                  {isOrderable && rankPositionOptions.length > 0 && (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="rank-position-label">Field Placement</InputLabel>
                      <Select
                        labelId="rank-position-label"
                        value={
                          rankPositionIndex <= rankPositionOptions.length - 1
                            ? rankPositionIndex
                            : (rankPositionOptions[rankPositionOptions.length - 1]?.value ?? 0)
                        }
                        onChange={(e) => setRankPositionIndex(Number(e.target.value))}
                        label="Field Placement"
                      >
                        {rankPositionOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                        Choose where this field appears relative to other fields.
                      </Typography>
                    </FormControl>
                  )}
                  {isPhotoField && (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <FormControl variant="outlined" sx={{ flex: "2 1 0%", minWidth: 120 }}>
                        <InputLabel id="photo-surcharge-label">Photo upload</InputLabel>
                        <Select
                          labelId="photo-surcharge-label"
                          value={photoSurchargeEnabled ? "have_surcharge" : "no_surcharge"}
                          onChange={(e) => {
                            const v = e.target.value === "have_surcharge";
                            setPhotoSurchargeEnabled(v);
                            if (!v) setPhotoSurchargeError("");
                          }}
                          label="Photo upload"
                        >
                          <MenuItem value="no_surcharge">No Surcharge</MenuItem>
                          <MenuItem value="have_surcharge">
                            Have Surcharge For Photo Upload
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {photoSurchargeEnabled && (
                        <TextField
                          label="Amount"
                          type="number"
                          value={photoSurchargeAmount}
                          onChange={(e) => {
                            setPhotoSurchargeAmount(e.target.value);
                            setPhotoSurchargeError("");
                          }}
                          onWheel={(e) => e.target?.blur?.()}
                          variant="outlined"
                          sx={{ flex: "1 1 0%", minWidth: 120 }}
                          inputProps={{ min: 0, step: 0.01 }}
                          error={Boolean(photoSurchargeError)}
                          helperText={photoSurchargeError}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyOutlinedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" flexDirection="column" gap={4}>
                  {isSelectField && (
                    <Box display="flex" flexDirection="column" gap={4}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel id="selection-type-label">Selection Type</InputLabel>
                        <Select
                          labelId="selection-type-label"
                          id="selection-type"
                          value={selectionType}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSelectionType(v);
                            if (v === "single") {
                              setMin(1);
                              setMax(1);
                            } else {
                              setMin(1);
                              setMax(max > 1 ? max : 2);
                            }
                          }}
                          label="Selection type"
                          disabled={
                            effectiveVariantKey === "flavor" || effectiveVariantKey === "filling"
                          }
                        >
                          <MenuItem value="single">Allow Single Selection</MenuItem>
                          <MenuItem value="multi">Allow Multiple Selections</MenuItem>
                        </Select>
                      </FormControl>
                      {selectionType === "multi" && (
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <TextField
                            label="Min Selections"
                            type="number"
                            value={min}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                setMin("");
                                return;
                              }
                              const v = parseInt(raw, 10);
                              if (!Number.isNaN(v)) {
                                setMin(Math.min(SELECT_MIN_MAX, Math.max(SELECT_MIN_MIN, v)));
                              }
                            }}
                            inputProps={{ min: SELECT_MIN_MIN, max: SELECT_MIN_MAX, step: 1 }}
                            variant="outlined"
                            sx={{ flex: 1, minWidth: 120 }}
                            disabled={
                              effectiveVariantKey === "flavor" || effectiveVariantKey === "filling"
                            }
                            helperText="Minimum selections allowed"
                          />
                          <TextField
                            label="Max Selections"
                            type="number"
                            value={max}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                setMax("");
                                return;
                              }
                              const v = parseInt(raw, 10);
                              if (!Number.isNaN(v)) {
                                setMax(Math.min(SELECT_MAX_MAX, Math.max(SELECT_MAX_MIN, v)));
                              }
                            }}
                            inputProps={{ min: SELECT_MAX_MIN, max: SELECT_MAX_MAX, step: 1 }}
                            variant="outlined"
                            sx={{ flex: 1, minWidth: 120 }}
                            disabled={
                              effectiveVariantKey === "flavor" || effectiveVariantKey === "filling"
                            }
                            helperText={
                              effectiveVariantKey === "flavor" || effectiveVariantKey === "filling"
                                ? "Not editable for this field type"
                                : "Maximum selections allowed"
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                  {isTextField && (
                    <TextField
                      label="Max length (characters)"
                      type="number"
                      value={max}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setMax("");
                          return;
                        }
                        const v = parseInt(raw, 10);
                        if (!Number.isNaN(v)) {
                          setMax(Math.min(TEXT_MAX_LENGTH_MAX, Math.max(TEXT_MAX_LENGTH_MIN, v)));
                        }
                      }}
                      inputProps={{
                        min: TEXT_MAX_LENGTH_MIN,
                        max: TEXT_MAX_LENGTH_MAX,
                        step: 1,
                      }}
                      variant="outlined"
                      fullWidth
                      // helperText="Maximum number of characters customers can enter"
                    />
                  )}
                  {isPhotoField && (
                    <TextField
                      label="Max photos"
                      type="number"
                      value={max}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setMax("");
                          return;
                        }
                        const v = parseInt(raw, 10);
                        if (!Number.isNaN(v)) {
                          setMax(Math.min(PHOTO_MAX_MAX, Math.max(PHOTO_MAX_MIN, v)));
                        }
                      }}
                      inputProps={{ min: PHOTO_MAX_MIN, max: PHOTO_MAX_MAX, step: 1 }}
                      variant="outlined"
                      fullWidth
                      // helperText="Maximum number of photos customers can upload"
                    />
                  )}
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(required)}
                          onChange={(e) => setRequired(Boolean(e.target.checked))}
                          color="primary"
                        />
                      }
                      label="Field is Required"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
          {((isAddMode && activeStep === 2) ||
            (!isAddMode && showFieldOptionsTab && tab === 1)) && (
            <Box>
              {isSelectField && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: 1.5,
                      mb: 3,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h3"
                        component="h2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Field Options
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Define the options for this field.
                      </Typography>
                    </Box>
                    <ProductFieldOption
                      category={capitalize(effectiveVariantKey || "field")}
                      mode="Add"
                      value=""
                      onChange={(data) => {
                        setOptions((prev) => [...prev, data]);
                      }}
                    >
                      <Button
                        type="button"
                        color="secondary"
                        variant="outlined"
                        startIcon={<AddIcon />}
                      >
                        Add Option
                      </Button>
                    </ProductFieldOption>
                  </Box>
                  {effectiveVariantKey &&
                    effectiveVariantKey !== "size" &&
                    showSizeOverrideGrid &&
                    sizeChart.length === 0 && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Add at least one size under Variant pricing to use the per-size availability
                        and pricing matrix.
                      </Alert>
                    )}
                  {showSizeOverrideGrid && options.length === 0 && (
                    <Alert severity="info" sx={{ mt: 0, mb: 2 }}>
                      Click + to add options for {effectiveVariant?.name}.
                    </Alert>
                  )}
                  {showSizeOverrideGrid && options.length > 0 && sizeChart.length > 0 && (
                    <VariantOptionSizeOverrideGrid
                      sizeChart={sizeChart}
                      options={options}
                      onCellClick={(optionIndex, size) => {
                        if (isPercentageSizeCell(options[optionIndex], size?.name)) {
                          setPercentOverrideBlocked(true);
                          return;
                        }
                        setSelectedOverrideCell({ optionIndex, size });
                      }}
                      onOptionEditDetails={(optionIndex) => setOptionEditIndex(optionIndex)}
                      onOptionEditPricing={(optionIndex) => setOptionPricingIndex(optionIndex)}
                      onOptionDelete={(optionIndex) => setOptionDeleteIndex(optionIndex)}
                    />
                  )}
                  <VariantOptionSizeOverrideCellDialog
                    open={selectedOverrideCell !== null}
                    onClose={() => setSelectedOverrideCell(null)}
                    selected={
                      selectedOverrideCell == null
                        ? null
                        : {
                            optionIndex: selectedOverrideCell.optionIndex,
                            sizeData: selectedOverrideCell.size,
                            sizeName: selectedOverrideCell.size?.name,
                            optionLabel: options[selectedOverrideCell.optionIndex]?.name,
                            option: options[selectedOverrideCell.optionIndex],
                          }
                    }
                    onSubmit={(newOption) => {
                      if (selectedOverrideCell == null) return;
                      const optionIndex = selectedOverrideCell.optionIndex;
                      setOptions((prev) => {
                        const next = [...prev];
                        next[optionIndex] = newOption;
                        return next;
                      });
                    }}
                  />
                  <Dialog
                    open={percentOverrideBlocked}
                    onClose={() => setPercentOverrideBlocked(false)}
                  >
                    <DialogTitle>Cannot edit override</DialogTitle>
                    <DialogContent sx={{ "&&": { pt: 3 } }}>
                      <Typography variant="body1">
                        Percentage cannot be overridden. Use the overflow menu to edit pricing for
                        this variant option.
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        fullWidth
                        onClick={() => setPercentOverrideBlocked(false)}
                      >
                        OK
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <ProductFieldOption
                    category={capitalize(effectiveVariantKey || "field")}
                    mode="Edit"
                    section="details"
                    title="Edit Option Details"
                    open={optionEditIndex !== null}
                    value={optionEditIndex === null ? {} : options[optionEditIndex]}
                    showDelete={false}
                    onClose={() => setOptionEditIndex(null)}
                    onChange={(data) => {
                      if (optionEditIndex === null) return;
                      const index = optionEditIndex;
                      setOptions((prev) => {
                        const next = [...prev];
                        next[index] = data;
                        return next;
                      });
                    }}
                  />
                  <ProductFieldOption
                    category={capitalize(effectiveVariantKey || "field")}
                    mode="Edit"
                    section="pricing"
                    title="Edit Pricing"
                    open={optionPricingIndex !== null}
                    value={optionPricingIndex === null ? {} : options[optionPricingIndex]}
                    showDelete={false}
                    notice={
                      optionPricingIndex !== null &&
                      getVariantOptionSizeOverrideCount(options[optionPricingIndex]) > 0
                        ? `Changing the pricing will remove the ${getVariantOptionSizeOverrideCount(
                            options[optionPricingIndex]
                          )} size-specific override(s) set for this option.`
                        : null
                    }
                    onClose={() => setOptionPricingIndex(null)}
                    onChange={(data) => {
                      if (optionPricingIndex === null) return;
                      const index = optionPricingIndex;
                      setOptions((prev) => {
                        const next = [...prev];
                        // Size overrides only reset when the standard pricing itself changes.
                        next[index] = isSameVariantOptionPricing(
                          prev[index]?.pricing,
                          data?.pricing
                        )
                          ? data
                          : clearVariantOptionSizeOverrides(data);
                        return next;
                      });
                    }}
                  />
                  <Dialog
                    open={optionDeleteIndex !== null}
                    onClose={() => setOptionDeleteIndex(null)}
                  >
                    <DialogTitle>Delete option?</DialogTitle>
                    <DialogContent sx={{ "&&": { pt: 3 } }}>
                      <Typography variant="body1">
                        {`Remove "${options[optionDeleteIndex]?.name || "this option"}" from this field?`}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        type="button"
                        color="secondary"
                        variant="outlined"
                        onClick={() => setOptionDeleteIndex(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          const index = optionDeleteIndex;
                          setOptions((prev) => {
                            const next = [...prev];
                            next.splice(index, 1);
                            return next;
                          });
                          setOptionDeleteIndex(null);
                        }}
                      >
                        Confirm
                      </Button>
                    </DialogActions>
                  </Dialog>
                  {!showSizeOverrideGrid && options.length === 0 && (
                    <Alert severity="info" sx={{ mt: 0, mb: 2 }}>
                      Click + to add options for {effectiveVariant?.name}.
                    </Alert>
                  )}
                  {!showSizeOverrideGrid && options.length > 0 && (
                    <List
                      dense
                      disablePadding
                      sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
                    >
                      {options.map((d, idx) => {
                        const surcharge = getVariantOptionSurchargeForDisplay(d);
                        const secondary = [d.description?.trim(), surcharge]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <ProductFieldOption
                            key={`${d.name}-${idx}`}
                            category={capitalize(effectiveVariantKey || "field")}
                            value={d}
                            onChange={(data) => {
                              setOptions((prev) => {
                                const next = [...prev];
                                if (data) {
                                  next[idx] = data;
                                } else {
                                  next.splice(idx, 1);
                                }
                                return next;
                              });
                            }}
                          >
                            <ListItemButton
                              sx={{
                                py: 1.5,
                                px: 2,
                                borderBottom: idx < options.length - 1 ? 1 : 0,
                                borderColor: "divider",
                                alignItems: "flex-start",
                              }}
                              disableRipple
                              component="div"
                            >
                              <ListItemText
                                primary={d.name}
                                secondary={secondary || null}
                                primaryTypographyProps={{ variant: "body1", fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: "body2" }}
                              />
                              <IconButton
                                type="button"
                                size="small"
                                aria-label={`Edit ${d.name}`}
                                sx={{ flexShrink: 0, mt: 0.25, color: "text.secondary" }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </ListItemButton>
                          </ProductFieldOption>
                        );
                      })}
                    </List>
                  )}
                </>
              )}
              {effectiveVariant?.field?.type === "text" && (
                <Typography variant="body1" color="textSecondary">
                  No options to configure.
                </Typography>
              )}
              {effectiveVariant?.field?.type === "photo" && (
                <Typography variant="body1" color="textSecondary">
                  No options to configure.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "stretch",
            gap: 2,
            px: 2,
            py: 2,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {!isAddMode && (
            <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
              <ButtonWithConfirmation
                fullWidth
                title="Delete field?"
                copy={`Removing "${variant.name}" will delete this field and all its options.`}
                onConfirm={handleDelete}
              >
                <Button
                  fullWidth
                  type="button"
                  variant="outlined"
                  color="error"
                  aria-label="Delete field"
                >
                  REMOVE THIS FIELD
                </Button>
              </ButtonWithConfirmation>
            </Box>
          )}
          {isAddMode && activeStep > 0 && (
            <Button
              fullWidth
              type="button"
              onClick={handleBack}
              color="secondary"
              variant="outlined"
              sx={{ flex: "1 1 0", minWidth: 0 }}
            >
              Back
            </Button>
          )}
          {isAddMode && activeStep < 2 && (
            <Button
              fullWidth
              type="button"
              onClick={handleNext}
              color="primary"
              variant="contained"
              disabled={!effectiveVariant || (activeStep === 1 && !canProceedFromDetails)}
              sx={{ flex: "1 1 0", minWidth: 0 }}
            >
              Next
            </Button>
          )}
          {((isAddMode && activeStep === 2) || !isAddMode) && (
            <Button
              fullWidth
              type="submit"
              color="primary"
              variant="contained"
              sx={{ flex: "1 1 0", minWidth: 0 }}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
